/**
 * 访问统计功能
 * 记录访问地区、当日访问量、总访问量
 * 数据保存在服务器后端
 */

// API 基础 URL
// 开发环境：使用完整 URL（因为前端和后端在不同端口）
// 生产环境：使用相对路径（通过 Nginx 代理）
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.API_BASE_URL) {
    return window.API_BASE_URL;
  }
  // 开发环境检测：如果当前是 localhost（任何端口），使用完整后端 URL
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:35001/api';
  }
  // 生产环境：使用相对路径
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();
const VISITOR_GEO_CACHE_KEY = 'visitor_geo_cache_v1';
const VISITOR_GEO_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LEAFLET_LOCAL_CSS_URL = new URL('../assets/leaflet/leaflet.css', import.meta.url).toString();
const LEAFLET_LOCAL_JS_URL = new URL('../assets/leaflet/leaflet.js', import.meta.url).toString();
const LEAFLET_CDN_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_CDN_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const MAP_ASSET_LOAD_TIMEOUT_MS = 5000;

let leafletLoadPromise = null;
let mapShouldLoad = false;
let latestMapVisits = [];
let currentStatsView = 'regions';
let isRegionListExpanded = false;
const DEFAULT_REGION_LIST_LIMIT = 10;

// 获取今天的日期字符串 (YYYY-MM-DD)
function getTodayString() {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

// 从服务器加载统计数据
async function loadStats() {
  const url = `${API_BASE_URL}/stats`;
  console.log('=== Loading Stats ===');
  console.log('URL:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // 添加 mode 和 credentials 以确保 CORS 正常工作
      mode: 'cors',
      credentials: 'omit'
    });
    
    console.log('Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      const text = await response.text();
      console.error('Response not OK:', text);
      throw new Error(`Failed to fetch stats: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ Stats loaded successfully:', data);
    console.log('Total visits:', data.totalVisits);
    console.log('Regions:', data.regions);
    return data;
  } catch (error) {
    console.error('❌ Failed to load stats:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    console.warn('提示：请确保后端服务器正在运行（npm run server）');
    console.warn('API URL:', API_BASE_URL);
    console.warn('尝试访问:', `${API_BASE_URL}/stats`);
    
    // 返回空数据
    const emptyStats = {
      totalVisits: 0,
      regions: {},
      dailyVisits: {},
      visits: []
    };
    console.log('Returning empty stats:', emptyStats);
    return emptyStats;
  }
}

function getUnknownLocation() {
  return {
    region: 'Unknown',
    countryCode: null,
    latitude: null,
    longitude: null
  };
}

function readCachedVisitorLocation() {
  try {
    const raw = localStorage.getItem(VISITOR_GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.location || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > VISITOR_GEO_CACHE_TTL_MS) {
      localStorage.removeItem(VISITOR_GEO_CACHE_KEY);
      return null;
    }
    if (!parsed.location.region || parsed.location.region === 'Unknown') return null;
    return parsed.location;
  } catch (error) {
    console.warn('Failed to read geo cache:', error.message);
    return null;
  }
}

function cacheVisitorLocation(location) {
  try {
    if (!location || !location.region || location.region === 'Unknown') return;
    localStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      location: {
        region: location.region,
        countryCode: location.countryCode || null,
        latitude: location.latitude || null,
        longitude: location.longitude || null
      }
    }));
  } catch (error) {
    console.warn('Failed to cache geo:', error.message);
  }
}

// 获取访问者地理位置（仅走后端，快速失败）
async function getVisitorLocation(timeoutMs = 1500) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}/geo`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal
    });
    if (!response.ok) {
      throw new Error(`Backend /geo failed: ${response.status}`);
    }
    const data = await response.json();
    const location = {
      region: data?.region || 'Unknown',
      countryCode: data?.countryCode || null,
      latitude: data?.latitude || null,
      longitude: data?.longitude || null
    };
    if (location.region !== 'Unknown') {
      cacheVisitorLocation(location);
    }
    return location;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('⏱️ Backend /geo timeout');
    } else {
      console.warn('Backend /geo request error:', error.message);
    }
    return getUnknownLocation();
  } finally {
    clearTimeout(timeoutId);
  }
}

function prefetchVisitorLocation() {
  if (readCachedVisitorLocation()) return;
  getVisitorLocation().then(location => {
    if (location.region !== 'Unknown') {
      console.log('✅ Cached visitor location for future visits:', location.region);
    }
  }).catch(error => {
    console.warn('Prefetch geo failed:', error.message);
  });
}

function ensureMapPlaceholder() {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer) return;
  if (window.visitMap) return;
  if (mapContainer.dataset.mapPlaceholderReady === '1') return;

  mapContainer.dataset.mapPlaceholderReady = '1';
  mapContainer.setAttribute('aria-busy', 'true');
  mapContainer.innerHTML = `
    <div class="stats-map-placeholder" style="display:flex;align-items:center;justify-content:center;height:100%;font-size:1.3rem;color:#666;">
      Loading map...
    </div>
  `;
}

function setStatsView(view) {
  const normalized = view === 'map' ? 'map' : 'regions';
  currentStatsView = normalized;

  const regionsPanel = document.getElementById('stats-panel-regions');
  const mapPanel = document.getElementById('stats-panel-map');
  const regionTab = document.getElementById('stats-tab-regions');
  const mapTab = document.getElementById('stats-tab-map');

  const showMap = normalized === 'map';
  if (regionsPanel) {
    regionsPanel.hidden = showMap;
    regionsPanel.classList.toggle('is-hidden', showMap);
  }
  if (mapPanel) {
    mapPanel.hidden = !showMap;
    mapPanel.classList.toggle('is-hidden', !showMap);
  }
  if (regionTab) {
    regionTab.classList.toggle('is-active', !showMap);
    regionTab.setAttribute('aria-selected', String(!showMap));
  }
  if (mapTab) {
    mapTab.classList.toggle('is-active', showMap);
    mapTab.setAttribute('aria-selected', String(showMap));
  }

  if (showMap) {
    ensureMapPlaceholder();
    mapShouldLoad = true;
    loadMapIfNeeded().then(() => {
      if (window.visitMap?.map) {
        setTimeout(() => window.visitMap.map.invalidateSize(), 0);
      }
    }).catch(error => {
      console.warn('Map load after tab switch failed:', error.message);
    });
  }
}

function initStatsViewTabs() {
  const tabs = document.querySelectorAll('[data-stats-view]');
  if (!tabs.length) return;
  tabs.forEach(tab => {
    if (tab.dataset.boundClick === '1') return;
    tab.dataset.boundClick = '1';
    tab.addEventListener('click', () => {
      setStatsView(tab.getAttribute('data-stats-view'));
    });
  });
  setStatsView('regions');
}

function ensureRegionToggleButton() {
  const regionListElement = document.getElementById('stats-regions');
  if (!regionListElement) return null;
  const parent = regionListElement.parentElement;
  if (!parent) return null;

  let button = document.getElementById('stats-regions-toggle');
  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.id = 'stats-regions-toggle';
    button.className = 'stats-regions-toggle';
    button.addEventListener('click', () => {
      isRegionListExpanded = !isRegionListExpanded;
      const statsRegionList = document.getElementById('stats-regions');
      if (!statsRegionList) return;
      let items = null;
      try {
        items = statsRegionList.dataset.regionSource ? JSON.parse(statsRegionList.dataset.regionSource) : null;
      } catch (error) {
        console.warn('Failed to parse region source cache:', error.message);
      }
      if (items && typeof items === 'object') {
        updateRegionList(items);
      }
    });
    parent.appendChild(button);
  }
  return button;
}

function updateMapPlaceholderText(text) {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer || window.visitMap) return;
  const placeholder = mapContainer.querySelector('.stats-map-placeholder');
  if (placeholder) placeholder.textContent = text;
}

function loadStylesheet(url, id, timeoutMs = MAP_ASSET_LOAD_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timerId = null;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      if (timerId) clearTimeout(timerId);
      fn();
    };

    if (id) {
      const existing = document.getElementById(id);
      if (existing) {
        if (existing.dataset.failed === '1') {
          existing.remove();
        } else {
          if (existing.dataset.loaded === '1') return resolve();
          existing.addEventListener('load', () => finish(resolve), { once: true });
          existing.addEventListener('error', () => finish(() => reject(new Error(`Failed to load ${url}`))), { once: true });
          timerId = setTimeout(() => finish(() => reject(new Error(`Timeout loading ${url}`))), timeoutMs);
          return;
        }
      }
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    if (id) link.id = id;
    link.addEventListener('load', () => {
      link.dataset.loaded = '1';
      finish(resolve);
    }, { once: true });
    link.addEventListener('error', () => {
      link.dataset.failed = '1';
      finish(() => reject(new Error(`Failed to load ${url}`)));
    }, { once: true });
    timerId = setTimeout(() => {
      link.dataset.failed = '1';
      finish(() => reject(new Error(`Timeout loading ${url}`)));
    }, timeoutMs);
    document.head.appendChild(link);
  });
}

function loadScript(url, id, timeoutMs = MAP_ASSET_LOAD_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timerId = null;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      if (timerId) clearTimeout(timerId);
      fn();
    };

    if (typeof window !== 'undefined' && window.L) return resolve();
    if (id) {
      const existing = document.getElementById(id);
      if (existing) {
        if (existing.dataset.failed === '1') {
          existing.remove();
        } else {
          if (existing.dataset.loaded === '1') return resolve();
          existing.addEventListener('load', () => finish(resolve), { once: true });
          existing.addEventListener('error', () => finish(() => reject(new Error(`Failed to load ${url}`))), { once: true });
          timerId = setTimeout(() => finish(() => reject(new Error(`Timeout loading ${url}`))), timeoutMs);
          return;
        }
      }
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    if (id) script.id = id;
    script.addEventListener('load', () => {
      script.dataset.loaded = '1';
      finish(resolve);
    }, { once: true });
    script.addEventListener('error', () => {
      script.dataset.failed = '1';
      finish(() => reject(new Error(`Failed to load ${url}`)));
    }, { once: true });
    timerId = setTimeout(() => {
      script.dataset.failed = '1';
      finish(() => reject(new Error(`Timeout loading ${url}`)));
    }, timeoutMs);
    document.head.appendChild(script);
  });
}

async function ensureLeafletLoaded() {
  if (typeof window !== 'undefined' && window.L) return;
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = (async () => {
    updateMapPlaceholderText('Loading map library...');
    try {
      await loadStylesheet(LEAFLET_LOCAL_CSS_URL, 'leaflet-css');
      await loadScript(LEAFLET_LOCAL_JS_URL, 'leaflet-js');
      if (typeof window === 'undefined' || !window.L) {
        throw new Error('Leaflet local script loaded but window.L is missing');
      }
      console.log('✅ Leaflet loaded from local assets');
      return;
    } catch (localError) {
      console.warn('Local Leaflet assets unavailable, falling back to CDN:', localError.message);
    }

    await loadStylesheet(LEAFLET_CDN_CSS_URL, 'leaflet-css');
    await loadScript(LEAFLET_CDN_JS_URL, 'leaflet-js');
    if (typeof window === 'undefined' || !window.L) {
      throw new Error('Leaflet CDN script loaded but window.L is missing');
    }
    console.log('✅ Leaflet loaded from CDN');
  })().catch(error => {
    leafletLoadPromise = null;
    throw error;
  });

  return leafletLoadPromise;
}

function setupLazyMapLoading() {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer || mapContainer.dataset.lazyObserverAttached === '1') return;

  ensureMapPlaceholder();
  mapContainer.dataset.lazyObserverAttached = '1';
  // 地图改为用户切换到 Map 视图后再加载，这里只做初始化占位。
}

async function loadMapIfNeeded() {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer || window.visitMap) return;
  if (!mapShouldLoad) return;

  ensureMapPlaceholder();
  updateMapPlaceholderText('Loading map...');

  try {
    await ensureLeafletLoaded();
    if (!window.visitMap) {
      initMap(latestMapVisits);
    }
  } catch (error) {
    console.error('❌ Failed to load Leaflet:', error);
    updateMapPlaceholderText('Map unavailable (network blocked)');
  }
}

// 记录访问（防止重复调用）
let isRecording = false;
let lastRecordTime = 0;
const MIN_RECORD_INTERVAL = 2000; // 最小记录间隔：2秒

// 记录访问
async function recordVisit() {
  // 防止重复调用
  if (isRecording) {
    console.log('⏸️ Already recording, skipping...');
    return;
  }
  
  const now = Date.now();
  if (now - lastRecordTime < MIN_RECORD_INTERVAL) {
    console.log('⏸️ Too soon since last record, skipping...');
    return;
  }
  
  // 检查今天是否已经记录过（使用 sessionStorage 防止刷新重复计数）
  const today = getTodayString();
  const sessionKey = `visited_${today}`;
  const visitTimestamp = sessionStorage.getItem(sessionKey);
  
  if (visitTimestamp) {
    const visitTime = parseInt(visitTimestamp, 10);
    const timeSinceVisit = now - visitTime;
    // 如果5分钟内已经记录过，不重复记录
    if (timeSinceVisit < 5 * 60 * 1000) {
      console.log('⏸️ Already visited today (within 5 minutes), only updating display');
      const stats = await loadStats();
      updateDisplay(stats);
      return;
    }
  }
  
  isRecording = true;
  lastRecordTime = now;
  
  try {
    // 访问计数不再等待地理定位，避免在网络不佳时阻塞页面
    const cachedLocation = readCachedVisitorLocation();
    const location = cachedLocation || getUnknownLocation();
    if (!cachedLocation) {
      prefetchVisitorLocation();
    }
    console.log('📍 Using location for visit:', location.region);
    
    // 发送到服务器
    const response = await fetch(`${API_BASE_URL}/visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(location)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to record visit: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // 标记今天已访问（记录时间戳）
    sessionStorage.setItem(sessionKey, now.toString());
    
    console.log('✅ Visit recorded successfully');
    
    // 更新显示
    updateDisplay(result.stats);
  } catch (error) {
    console.error('❌ Failed to record visit:', error);
    console.warn('提示：请确保后端服务器正在运行（npm run server）');
    // 即使失败也尝试加载现有数据
    const stats = await loadStats();
    updateDisplay(stats);
  } finally {
    isRecording = false;
  }
}

// 更新显示
function updateDisplay(stats) {
  console.log('=== Updating Display ===');
  console.log('Stats object:', stats);
  console.log('Stats type:', typeof stats);
  console.log('Stats keys:', Object.keys(stats || {}));
  
  if (!stats) {
    console.error('Stats is null or undefined!');
    return;
  }
  
  const today = getTodayString();
  console.log(`Today string: "${today}"`);
  console.log(`dailyVisits object:`, stats.dailyVisits);
  console.log(`dailyVisits keys:`, Object.keys(stats.dailyVisits || {}));
  
  // 检查日期匹配
  const todayVisits = stats.dailyVisits?.[today] || 0;
  const totalVisits = stats.totalVisits || 0;
  
  console.log(`Today: ${today}`);
  console.log(`Today visits (from dailyVisits[${today}]): ${todayVisits}`);
  console.log(`Total visits: ${totalVisits}`);
  console.log(`Regions:`, stats.regions);
  console.log(`Regions keys:`, Object.keys(stats.regions || {}));
  
  // 更新数字显示
  const todayElement = document.getElementById('stats-today');
  const totalElement = document.getElementById('stats-total');
  
  console.log('Today element:', todayElement);
  console.log('Total element:', totalElement);
  
  if (todayElement) {
    todayElement.textContent = todayVisits.toLocaleString();
    console.log('✅ Updated today visits to:', todayVisits);
  } else {
    console.error('❌ stats-today element not found!');
  }
  
  if (totalElement) {
    totalElement.textContent = totalVisits.toLocaleString();
    console.log('✅ Updated total visits to:', totalVisits);
  } else {
    console.error('❌ stats-total element not found!');
  }
  
  // 更新地区列表
  console.log('Updating region list with:', stats.regions);
  updateRegionList(stats.regions || {});
  
  // 更新地图
  console.log('Updating map with visits:', stats.visits);
  updateMap(stats.visits || []);
  
  console.log('=== Display Update Complete ===');
}

// 更新地区列表显示
function updateRegionList(regions) {
  const regionListElement = document.getElementById('stats-regions');
  if (!regionListElement) {
    console.warn('stats-regions element not found');
    return;
  }
  
  console.log('Updating region list with:', regions);
  
  // 检查是否有数据
  if (!regions || Object.keys(regions).length === 0) {
    // 使用国际化文本，但这里暂时用硬编码，因为i18n可能还没准备好
    const noDataText = document.documentElement.lang === 'zh' 
      ? '暂无数据（请确保后端服务器正在运行）'
      : 'No data (Please ensure backend server is running)';
    regionListElement.innerHTML = `<li class="stats-region-item">${noDataText}</li>`;
    regionListElement.dataset.regionSource = JSON.stringify({});
    const toggleBtn = document.getElementById('stats-regions-toggle');
    if (toggleBtn) toggleBtn.hidden = true;
    return;
  }
  
  // 按访问量排序
  const sortedRegions = Object.entries(regions)
    .sort((a, b) => b[1] - a[1]);
  
  if (sortedRegions.length === 0) {
    const noDataText = document.documentElement.lang === 'zh' 
      ? '暂无数据'
      : 'No data';
    regionListElement.innerHTML = `<li class="stats-region-item">${noDataText}</li>`;
    regionListElement.dataset.regionSource = JSON.stringify({});
    const toggleBtn = document.getElementById('stats-regions-toggle');
    if (toggleBtn) toggleBtn.hidden = true;
    return;
  }
  
  const total = Object.values(regions).reduce((a, b) => a + b, 0);
  console.log(`Total visits: ${total}, Regions: ${sortedRegions.length}`);
  regionListElement.dataset.regionSource = JSON.stringify(regions);

  const shouldShowAll = isRegionListExpanded;
  const visibleRegions = shouldShowAll
    ? sortedRegions
    : sortedRegions.slice(0, DEFAULT_REGION_LIST_LIMIT);
  
  regionListElement.innerHTML = visibleRegions
    .map(([region, count]) => {
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      return `
        <li class="stats-region-item">
          <span class="stats-region-name">${region}</span>
          <span class="stats-region-count">${count}</span>
          <span class="stats-region-bar">
            <span class="stats-region-bar-fill" style="width: ${percentage}%"></span>
          </span>
        </li>
      `;
    })
    .join('');

  const toggleBtn = ensureRegionToggleButton();
  if (!toggleBtn) return;
  const hasMore = sortedRegions.length > DEFAULT_REGION_LIST_LIMIT;
  toggleBtn.hidden = !hasMore;
  if (!hasMore) {
    isRegionListExpanded = false;
    return;
  }
  const isZh = document.documentElement.lang === 'zh';
  toggleBtn.textContent = isRegionListExpanded
    ? (isZh ? '收起地区列表' : 'Show Top 10')
    : (isZh ? `展开全部（${sortedRegions.length}）` : `Show All (${sortedRegions.length})`);
  toggleBtn.setAttribute('aria-expanded', String(isRegionListExpanded));
}

// 更新地图显示
function updateMap(visits) {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer) return;
  latestMapVisits = Array.isArray(visits) ? visits : [];
  
  // 如果地图已经初始化，更新标记
  if (window.visitMap) {
    // 清除现有标记
    window.visitMap.markers.forEach(marker => marker.remove());
    window.visitMap.markers = [];
    
    // 添加新标记
    addMarkersToMap(window.visitMap.map, visits);
    return;
  }
  
  ensureMapPlaceholder();
  setupLazyMapLoading();
  if (mapShouldLoad || currentStatsView === 'map') {
    mapShouldLoad = true;
    loadMapIfNeeded();
  }
}

// 初始化地图
function initMap(visits) {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer) return;
  
  // Leaflet 由懒加载器动态注入
  if (typeof L === 'undefined') {
    console.warn('Leaflet is not loaded yet');
    return;
  }
  mapContainer.innerHTML = '';
  mapContainer.removeAttribute('aria-busy');
  
  // 显示全球，但通过边界限制防止左右无限循环拖动
  const worldBounds = L.latLngBounds(
    L.latLng(-70, -179.5),
    L.latLng(80, 179.5)
  );

  const map = L.map('visit-map', {
    worldCopyJump: false,
    maxBounds: worldBounds,
    maxBoundsViscosity: 0.9,
    minZoom: 2,
    maxZoom: 6
  }).setView([20, 10], 2);
  
  // 添加 OpenStreetMap 瓦片图层
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);

  map.fitBounds(worldBounds, { padding: [8, 8] });
  
  // 存储地图和标记数组
  window.visitMap = {
    map: map,
    markers: []
  };
  
  // 添加标记
  addMarkersToMap(map, visits);
}

// 添加标记到地图
function addMarkersToMap(map, visits) {
  if (!window.visitMap) return;
  
  // 按国家/地区分组统计
  const locationCounts = {};
  visits.forEach(visit => {
    const key = visit.latitude && visit.longitude 
      ? `${visit.latitude},${visit.longitude}`
      : visit.region;
    
    if (!locationCounts[key]) {
      locationCounts[key] = {
        count: 0,
        region: visit.region,
        latitude: visit.latitude,
        longitude: visit.longitude
      };
    }
    locationCounts[key].count++;
  });
  
  // 为每个位置添加标记
  Object.values(locationCounts).forEach(location => {
    let lat = location.latitude;
    let lng = location.longitude;
    
    // 如果没有坐标，尝试从国家名称获取（使用简单的映射）
    if (!lat || !lng) {
      const coords = getCountryCoordinates(location.region);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      } else {
        return; // 跳过没有坐标的位置
      }
    }
    
    // 创建标记
    const marker = L.circleMarker([lat, lng], {
      radius: Math.min(Math.max(location.count * 2, 5), 30), // 根据访问量调整大小
      fillColor: '#02aab0',
      color: '#fff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.7
    }).addTo(map);
    
    // 添加弹出窗口
    marker.bindPopup(`
      <strong>${location.region}</strong><br>
      访问量: ${location.count}
    `);
    
    window.visitMap.markers.push(marker);
  });
}

// 简单的国家名称到坐标映射（常用国家）
function getCountryCoordinates(countryName) {
  const countryMap = {
    'China': { lat: 35.8617, lng: 104.1954 },
    'United States': { lat: 37.0902, lng: -95.7129 },
    'United Kingdom': { lat: 55.3781, lng: -3.4360 },
    'Canada': { lat: 56.1304, lng: -106.3468 },
    'Australia': { lat: -25.2744, lng: 133.7751 },
    'Germany': { lat: 51.1657, lng: 10.4515 },
    'France': { lat: 46.2276, lng: 2.2137 },
    'Japan': { lat: 36.2048, lng: 138.2529 },
    'South Korea': { lat: 35.9078, lng: 127.7669 },
    'India': { lat: 20.5937, lng: 78.9629 },
    'Brazil': { lat: -14.2350, lng: -51.9253 },
    'Russia': { lat: 61.5240, lng: 105.3188 },
    'Singapore': { lat: 1.3521, lng: 103.8198 },
    'Hong Kong': { lat: 22.3193, lng: 114.1694 },
    'Taiwan': { lat: 23.6978, lng: 120.9605 },
    'Unknown': null
  };
  
  return countryMap[countryName] || null;
}

// 初始化统计功能
export default function initVisitStats() {
  console.log('=== Visit Stats Initialization ===');
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('Current URL:', window.location.href);
  console.log('Hostname:', window.location.hostname);
  console.log('Port:', window.location.port);
  
  // 等待 DOM 完全加载
  const initStats = () => {
    console.log('DOM ready, initializing stats...');
    initStatsViewTabs();
    
    // 先加载一次数据并显示（不记录访问，只是显示已有数据）
    loadStats().then(stats => {
      console.log('Initial stats loaded:', stats);
      updateDisplay(stats);
    }).catch(error => {
      console.error('Failed to load initial stats:', error);
    });
    
    // 然后记录访问
    console.log('Recording visit...');
    recordVisit();
    
    // 定期更新显示
    setInterval(async () => {
      console.log('Periodic update...');
      const stats = await loadStats();
      updateDisplay(stats);
    }, 10000); // 每10秒更新一次
  };
  
  if (document.readyState === 'loading') {
    console.log('Waiting for DOM to load...');
    document.addEventListener('DOMContentLoaded', initStats);
  } else {
    console.log('DOM already loaded');
    // 延迟一点确保所有元素都已渲染
    setTimeout(initStats, 100);
  }
}
